import React from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { CheckboxV2, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@shopflo/ui'
import { CaretDown, Cube } from '@phosphor-icons/react'
import { currencyFormatter } from 'src/lib/utils/formatters'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import { ProductIcon } from 'src/components/discounts/v2/common/ProductIcons'
import { ProductStatusTag } from './ProductStatusTag'
import { classNames } from 'src/lib/utils/helpers'

interface ProductRowProps {
  product: CatalogueDataType
  isExpanded: boolean
  isSelected: boolean
  isIndeterminate: boolean
  selectedVariantIds?: Set<string>
  catalogueType: 'COLLECTION' | 'PRODUCT' | 'VARIANT' | 'PRODUCT_AND_VARIANT'
  disabledVariants?: {
    ids: Set<string>
    reason: string
    label?: string
  }
  onToggleProduct: (product: CatalogueDataType) => void
  onToggleVariant: (product: CatalogueDataType, variant: CatalogueDataType) => void
  onToggleExpand: (productId: string) => void
}

const ProductRow = React.memo(
  ({
    product,
    isExpanded,
    isSelected,
    isIndeterminate,
    selectedVariantIds,
    catalogueType,
    disabledVariants,
    onToggleProduct,
    onToggleVariant,
    onToggleExpand
  }: ProductRowProps) => {
    const hasVariants = product.variants && product.variants.length > 0
    const isVariantSelectionEnabled = catalogueType === 'PRODUCT_AND_VARIANT' || catalogueType === 'VARIANT'

    const isVariantDisabled = (variantId?: string) => !!variantId && !!disabledVariants?.ids?.has(variantId)

    // A product with every variant already claimed cannot be selected at all.
    const areAllVariantsDisabled =
      !!disabledVariants && !!hasVariants && product.variants!.every(variant => isVariantDisabled(variant.id as string))

    return (
      <div>
        {/* Fade column, so the "Already used" label stays legible. */}
        <div
          className={classNames(
            'grid grid-cols-[3fr_1fr_1fr] gap-4 py-1 items-center ',
            areAllVariantsDisabled ? 'opacity-60' : ''
          )}
        >
          <div className='flex items-center gap-3'>
            <CheckboxV2
              size='sm'
              checked={isSelected}
              indeterminate={isIndeterminate}
              disabled={areAllVariantsDisabled}
              onCheckedChange={() => !areAllVariantsDisabled && onToggleProduct(product)}
            />
            {product.imageUrl ? (
              <img
                src={product.imageUrl}
                alt={product.description || 'Product'}
                className='w-9 h-9 rounded object-cover'
              />
            ) : (
              <div>
                <ProductIcon wrapperSize='md' />
              </div>
            )}
            <div
              className='flex items-center gap-2 cursor-pointer'
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                onToggleExpand(product.id as string)
              }}
            >
              <span className='text-text-sm-medium text-zinc-800'>{product.description}</span>
              {product.status && <ProductStatusTag status={product.status} />}
              {hasVariants && (
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                >
                  <Icon
                    IconComponent={CaretDown}
                    size='xxxs'
                    className='text-zinc-500'
                    iconProps={{ weight: 'bold' }}
                  />
                </motion.div>
              )}
            </div>
          </div>
          {areAllVariantsDisabled ? (
            <div className='col-span-2 pr-1 text-right text-text-xs-medium text-zinc-500'>
              {disabledVariants?.label ?? 'Already used'}
            </div>
          ) : (
            <>
              <div className='text-right text-text-xs-regular text-zinc-600'>
                {!hasVariants && product.variants?.[0]?.inventory}
              </div>
              <div className='text-right text-text-xs-regular text-zinc-600'>
                {!hasVariants && product.variants?.[0]?.price && currencyFormatter(product.variants[0].price)}
              </div>
            </>
          )}
        </div>

        {/* Variants List */}
        <AnimatePresence initial={false}>
          {hasVariants && isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className='bg-zinc-50 rounded-lg my-1 overflow-hidden'
            >
              {product.variants!.map(variant => {
                const isVariantSelected = selectedVariantIds?.has(variant.id as string) || false
                const isDisabled = isVariantDisabled(variant.id as string)

                const row = (
                  <div
                    className={classNames(
                      'grid grid-cols-[3fr_1fr_1fr] gap-4 py-1.5 px-2 items-center',
                      isDisabled ? 'opacity-60 cursor-not-allowed' : ''
                    )}
                  >
                    <div className='flex items-center gap-2 pl-7'>
                      {isVariantSelectionEnabled && (
                        <CheckboxV2
                          size='sm'
                          checked={isVariantSelected}
                          disabled={isDisabled}
                          onCheckedChange={() => !isDisabled && onToggleVariant(product, variant)}
                        />
                      )}
                      <Icon IconComponent={Cube} size='xs' className='text-zinc-400' />
                      <span className='text-text-sm-regular text-zinc-600'>{variant.description}</span>
                    </div>

                    <div className='text-right text-text-xs-regular text-zinc-600'>{variant.inventory}</div>
                    <div className='text-right text-text-xs-regular text-zinc-600'>
                      {currencyFormatter(variant.price)}
                    </div>
                  </div>
                )

                if (!isDisabled) return <div key={variant.id}>{row}</div>

                return (
                  <Tooltip key={variant.id} delayDuration={0}>
                    <TooltipTrigger asChild>
                      <div>{row}</div>
                    </TooltipTrigger>
                    <TooltipContent side='right' className='max-w-[260px]'>
                      {disabledVariants?.reason}
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )
  }
)

export default ProductRow
